import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@habits/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Not a hypothesis: measured in
 * `space-academy-app` (same canon, `activity` table of the local stack) and
 * reproduced in `listto-app` (`9ae347b6`) — the mechanism: when the CRUD
 * queue empties, PowerSync **rebuilds the local view from the last
 * checkpoint**. If the checkpoint does not yet bring the newly created row, the row
 * is **ABSENT during that instant** — and an UPDATE against it writes
 * nothing, generates no op, and **does not fail**.
 *
 * ── Why this repo has its own risk ──────────────────────────────────────────
 * `habit-streak` revolves around streaks: `habit_completion` is the table for
 * daily check-ins and the streak counter — losing that UPDATE silently is the
 * most costly product damage (a broken streak cannot be reconstructed after the fact).
 * That is why mutations in `habits/mutations.ts` come first.
 *
 * Why absence of error proves nothing here: the server write-path returns
 * **2xx always by design** (a 4xx would block the client queue forever).
 * That case is a DIFFERENT route —there the server rejects and remains silent,
 * here the local UPDATE does not match— but the failure mode is the same: a
 * user write that disappears without a trace.
 *
 * Contract of this module: write, **REREAD**, compare. If the value did not
 * land it retries with backoff; if upon exhaustion it still has not landed, it
 * **THROWS** — caller communicates it instead of leaving user staring at a
 * field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberate, see `habits/mutations.ts`) ──
 * Hard `DELETE`s (`deleteHabitMutation`, `deleteReminderMutation`, and the
 * un-checking `DELETE`s in `setCompletionMutation` /
 * `incrementCompletionAmountMutation`) are not verified: their natural check
 * would be "the row is no longer there", and "absent" is precisely the ambiguous state of this
 * window. `spendStreakProtectorMutation` is not verified either: the client never creates a
 * `streak_protector` locally (it is granted by the server and arrives via sync), so
 * its row is already in the checkpoint before the client can attempt
 * to spend it — it does not experience the window this module fixes.
 *
 * ── Second class: upsert deciding INSERT-vs-UPDATE with own SELECT ───────────
 *
 * `patchRowVerified`/`waitForRowPresent` protect write against a row
 * whose `id` is ALREADY known. `habit_completion` does not always have it: the daily
 * record is identified by CONTENT (`habit_id` + `date`), and the SELECT that
 * decides "is there already a completion today?" falls into the SAME checkpoint window — if
 * the row was created moments earlier and has not landed yet, the SELECT sees it absent and
 * the code takes the INSERT branch. The data is not lost: the row is DUPLICATED (there is
 * no `UNIQUE(habit_id, date)` preventing it). Characterized with tests in
 * `habits/__tests__/mutations.test.ts` before having a recipe; now it has one:
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §3 documents two paths
 * (explicit `isNew` from caller, or `readMatchingRowVerified`) and the criteria
 * for choosing. Here the caller does NOT know in advance if the day already has
 * a completion — the key is by content — so it is closed with
 * `readMatchingRowVerified`, brought as-is from `stockpilot-app@b8c4bc8a`
 * (also housed in the canon `listto-app@8d4240fd`).
 */

/**
 * Retry backoff. Sums ≈ 6.05 s: more than covers the round trip of a
 * checkpoint without leaving a quick interaction —marking today's habit—
 * hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Delays between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that failed to land in view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is by
  // primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when comparing against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * fields and retries as long as they have not landed. Throws `LocalWriteLostError`
 * if they are still missing upon exhausting the backoff.
 *
 * `updated_at` is set by this function on each attempt (and is not compared: it
 * changes on every pass); `patch` must bring only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * Not currently used from `habits/mutations.ts` (there is no sibling
 * `UPDATE`+`INSERT` inside a transaction here, unlike `toggleTodoMutation`
 * in `listto-app`), but remains exported: it is part of the module contract
 * and any future mutation combining an UPDATE with a dependent INSERT inside
 * a transaction will need it (retrying the bare UPDATE alone would not work:
 * it would sleep with the write lock held).
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Rereads, with same backoff as rest of module, a row
 * identified by CONTENT instead of by `id` — the case of `habit_completion`
 * (`habit_id` + `date` key): an upsert deciding INSERT-vs-UPDATE by checking
 * its own content columns, without any `id` known in advance. See
 * the "Second class" note above.
 *
 * ALWAYS called before opening a transaction that decides INSERT-vs-UPDATE,
 * and NEVER inside it — same reason as `waitForRowPresent`: retrying with
 * write lock already held sleeps entire transaction, not just
 * read. When several rows need resolving at once, resolve them in
 * PARALLEL (`Promise.all`) before the transaction: this way a genuinely
 * new row does not pay the backoff latency of another row that did need
 * retrying.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting backoff
 * is NOT an error here: genuine absence (first time touching that
 * content combination) is VALID result, so this function
 * returns `undefined` instead of throwing, and caller proceeds to INSERT
 * exactly as if it had not retried.
 *
 * `read` must repeat SAME query on each attempt — a function that
 * rebuilds the `where` on `db`, not an already-resolved promise reused
 * without touching the database again.
 */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}

/**
 * ── Third piece: do not pay backoff when nothing suggests a window ──────────
 *
 * `readMatchingRowVerified` cannot distinguish "hidden by checkpoint" from
 * "genuinely new" without exhausting backoff — and pays it in BOTH cases. For
 * a content key (`habit_id`+`date`, `sku`+`warehouse_id`, ...) the case
 * "genuinely new" is the MOST common (first touch), so exhausting
 * backoff there is pure cost: ~6.05 s by default to decide something a
 * single read already answers just as well ("not found").
 *
 * The only time there IS reason to suspect window is when THIS
 * same process just INSERTed a row for that key — there, and only there,
 * the checkpoint may not have reached that local write yet.
 * `markRecentContentInsert` leaves that mark (in memory, per process —
 * lost on reload, which is exactly when it is no longer needed: the view
 * will already have rebuilt from a newer checkpoint); short TTL because
 * past margin, either it landed or backoff itself would not have
 * caught it.
 *
 * Candidate for promotion to canon: the same cost class is accepted by
 * `stockpilot-app` in `adjustStockMutation` with the same primitive.
 */
const RECENT_CONTENT_INSERT_TTL_MS = 10_000;
const recentContentInserts = new Map<string, number>();

/** Marks that THIS process just INSERTed a row for `key` (content key). */
export function markRecentContentInsert(key: string): void {
  recentContentInserts.set(key, Date.now());
}

function hasRecentContentInsert(key: string): boolean {
  const insertedAt = recentContentInserts.get(key);
  if (insertedAt === undefined) {
    return false;
  }
  if (Date.now() - insertedAt > RECENT_CONTENT_INSERT_TTL_MS) {
    recentContentInserts.delete(key);
    return false;
  }
  return true;
}

/**
 * `readMatchingRowVerified`, but only pays backoff when there is a live mark
 * from `markRecentContentInsert` for `key`. Without mark: single read,
 * without retries — as valid as exhausting backoff to decide "not found"
 * when nothing suggests otherwise. With mark: full backoff, same as
 * before; if the row is found, clear the mark (no window to fear
 * for the next touch of the same key).
 */
export async function readMatchingRowVerifiedIfSuspect<T>(
  key: string,
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  if (!hasRecentContentInsert(key)) {
    const [row] = await read();
    return row;
  }
  const row = await readMatchingRowVerified(read, options);
  if (row) {
    recentContentInserts.delete(key);
  }
  return row;
}
